import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState, useDeferredValue } from "react";
import {
  Add as AddIcon,
  BusinessRounded,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RefreshRounded,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  createProperty,
  deleteProperty,
  getProperties,
  updateProperty,
  type PropertyRecord,
} from "../api/properties";
import { PropertyModal } from "../components/PropertyModal";
import { useNotifications } from "../components/NotificationProvider";

export function PropertiesPage() {
  const [properties, setProperties] = useState<PropertyRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyRecord | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] =
    useState<PropertyRecord | null>(null);

  const { showAlert, showSnackbar } = useNotifications();

  const loadProperties = async () => {
    setIsLoading(true);
    try {
      const data = await getProperties();
      setProperties(data);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load properties",
        message: "Please refresh and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadProperties();
  }, []);

  const filteredProperties = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    if (!term) return properties;

    return properties.filter((property) => {
      const haystack = [
        property.property_name,
        property.city,
        property.postcode,
        property.address_line_1,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [properties, deferredSearchTerm]);

  const openCreateModal = () => {
    setSelectedProperty(null);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const openEditModal = (property: PropertyRecord) => {
    setSelectedProperty(property);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleCreateOrUpdate = async (payload: Record<string, unknown>) => {
    setIsLoading(true);
    try {
      if (modalMode === "edit" && selectedProperty) {
        await updateProperty(selectedProperty.id, payload);
        showSnackbar({
          title: "Property updated",
          message: "The property has been updated successfully.",
          variant: "success",
        });
      } else {
        await createProperty(payload);
        showSnackbar({
          title: "Property created",
          message: "The property has been added successfully.",
          variant: "success",
        });
      }
      await loadProperties();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Please try again.";
      showAlert({ title: "Property action failed", message, variant: "error" });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirmation = (property: PropertyRecord) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!propertyToDelete) return;
    setDeleteDialogOpen(false);

    setIsLoading(true);
    try {
      await deleteProperty(propertyToDelete.id);
      await loadProperties();
      showSnackbar({
        title: "Property deleted",
        message: "The property was removed.",
        variant: "info",
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Please try again.";
      showAlert({
        title: "Unable to delete property",
        message,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      setPropertyToDelete(null);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", position: "relative" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -8,
            left: 0,
            right: 0,
            borderRadius: 6,
          }}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 2, md: 3 },
          bgcolor: "background.paper",
          boxShadow: "0 10px 35px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by property, city, or postcode..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              maxHeight: 40,
              "& .MuiOutlinedInput-root": { borderRadius: 6 },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh properties">
              <IconButton
                onClick={() => void loadProperties()}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 6,
                }}
              >
                <RefreshRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              disableElevation
              sx={{ borderRadius: 6, px: 3, whiteSpace: "nowrap", height: 40 }}
            >
              Add property
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 6, p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Properties ({filteredProperties.length})
        </Typography>

        <Stack spacing={2}>
          {filteredProperties.map((property) => (
            <Card
              key={property.id}
              variant="outlined"
              sx={{
                borderRadius: 6,
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow: 2,
                  borderColor: "primary.main",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Box
                      sx={{
                        p: 1.1,
                        borderRadius: 6,
                        bgcolor: "rgba(33, 82, 184, 0.08)",
                        color: "primary.main",
                      }}
                    >
                      <BusinessRounded fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {property.property_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25 }}
                      >
                        {property.address_line_1}, {property.city},{" "}
                        {property.postcode}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1.25, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={`${property.total_rooms} rooms`}
                      size="small"
                      color="primary"
                      variant="filled"
                      sx={{ fontWeight: 600, borderRadius: 6 }}
                    />
                    <Chip
                      label={property.city}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, borderRadius: 6 }}
                    />
                  </Stack>
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  alignSelf={{ xs: "flex-end", sm: "center" }}
                >
                  <Tooltip title="Edit property">
                    <IconButton
                      color="primary"
                      onClick={() => openEditModal(property)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete property">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteConfirmation(property)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {filteredProperties.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              No properties match your current search.
            </Typography>
          </Box>
        )}
      </Paper>

      <PropertyModal
        open={isModalOpen}
        mode={modalMode}
        initialValue={selectedProperty ?? undefined}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateOrUpdate}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{ sx: { borderRadius: 6 } }}
      >
        <DialogTitle id="delete-dialog-title">Confirm deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete{" "}
            <strong>{propertyToDelete?.property_name}</strong>? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            autoFocus
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
